import { Box, Grid, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, styled, Dialog, DialogContent, Button } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import theme from "../../theme";
import CommonFormField from "../../common/components/CommonFormField";
import CommonButton from "../../common/components/CommonButton";
import SaveIcon from '@mui/icons-material/Save';
import { validateEmail } from "../../common/const";
import { BorderColor, Close, DeleteForever, WatchLater } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { setLoading } from "../../features/authSlice";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { SkipIntakeForm, SubmitIntakeForm } from "../../api/authApi";
import moment from "moment";
import { getIntakeEventByInvite } from "../../api/sessionApi";

const StyledDialog = styled(Dialog)({
    '& .MuiDialog-paper': {
        borderRadius: '16px',
        maxWidth: '540px',
        width: '100%',
    },
});

const Cell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    padding: 5,
    textAlign: 'center',
    fontSize: 13,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
    background: theme.palette.secondary.main,
    color: theme.palette.primary.main
}));

const InnerCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    padding: 5,
    textAlign: 'center',
    borderRight: `1px solid ${theme.palette.background.secondary}`,
}));

const IntakeForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.auth);
    const searchParams = new URLSearchParams(location.search);
    const [eventData, setEventData] = useState(null);

    // Extract all parameters
    const details = {
        uri: searchParams.get('uri'),
    };
    const [formData, setFormData] = useState({
        academic: {
            studentname: '',
            schoolname: '',
            grade: '',
            email: '',
        }
    });
    const [errors, setErrors] = useState({});
    const [courses, setCourses] = useState([]);
    const [activities, setActivities] = useState([]);
    const [bookSessionDialog, setBookSessionDialog] = useState(false);

    const handleCloseAll = () => {
        navigate('/login');
        setBookSessionDialog(false);
        setFormData({
            academic: {
                studentname: '',
                schoolname: '',
                grade: '',
                email: '',
            }
        });
        setCourses([]);
        setActivities([]);
    };

    const validateField = (name, value) => {
        switch (name) {
            case 'studentname':
                if (!value) return 'Student Name is required';
                return '';
            case 'email':
                if (!value) return 'Student Email is required';
                if (!validateEmail(value)) return 'Please enter a valid email address';
                return '';
            case 'schoolname':
                if (!value) return 'School Name is required';
                return '';
             case 'grade':
                // Check if empty
                if (!value && value !== 0) return 'Grade is required';

                // Convert to string for uniform handling
                const gradeStr = String(value).trim();
                if (gradeStr === '') return 'Grade is required';

                // Check for valid grade formats

                // Option 1: Numeric grades (0-100)
                const numericGrade = Number(gradeStr);
                if (!isNaN(numericGrade)) {
                    if (numericGrade < 0) return 'Grade cannot be negative';
                    if (numericGrade > 100) return 'Grade cannot exceed 100';
                    if (numericGrade < 60) return 'Grade is below passing threshold';
                    return '';
                }

                // Option 2: Letter grades (A, B, C, D, F with +/-)
                const letterGradePattern = /^[A-F][+-]?$/i;
                if (letterGradePattern.test(gradeStr)) {
                    const upperGrade = gradeStr.toUpperCase();
                    if (upperGrade === 'F') return 'Grade F is failing';
                    return '';
                }

                // Option 3: Grade levels (Kindergarten, 1st, 2nd, etc.)
                const gradeLevelPattern = /^(K|Kindergarten|[1-9]|1[0-2])(st|nd|rd|th)?$/i;
                if (gradeLevelPattern.test(gradeStr)) {
                    return '';
                }

                // Option 4: GPA format (0.0 - 4.0)
                const gpaPattern = /^[0-4](\.\d{1,2})?$/;
                if (gpaPattern.test(gradeStr)) {
                    const gpa = parseFloat(gradeStr);
                    if (gpa < 2.0) return 'GPA is below minimum requirement';
                    return '';
                }

                return 'Please enter a valid grade (e.g., 85, A, B+, 3.5, 10th)';

            default:
                return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            academic: {
                ...prev.academic,
                [name]: value,
            },
        }));

        // Clear error for the field being changed
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData.academic).forEach(key => {
            const error = validateField(key, formData.academic[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    // Course handlers
    const handleAddCourse = () => {
        const newId = courses.length > 0 ? Math.max(...courses.map(c => c.id)) + 1 : 1;
        setCourses([...courses, {
            id: newId,
            grade: '',
            sem1: '',
            fullYearGrade1: '',
            sem2: '',
            fullYearGrade2: '',
            totalSem: '',
            counselor: '',
            isEditing: true
        }]);
    };

    const handleEditCourse = (id) => {
        setCourses(courses.map(course =>
            course.id === id ? { ...course, isEditing: true } : course
        ));
    };

    const handleSaveCourse = (id) => {
        setCourses(courses.map(course =>
            course.id === id ? { ...course, isEditing: false } : course
        ));
    };

    const handleCancelCourse = (id) => {
        setCourses(courses.map(course =>
            course.id === id ? { ...course, isEditing: false } : course
        ));
    };

    const handleDeleteCourse = (id) => {
        setCourses(courses.filter(course => course.id !== id));
    };

    const handleCourseChange = (id, field, value) => {
        setCourses(courses.map(course =>
            course.id === id ? { ...course, [field]: value } : course
        ));
    };

    // Activity handlers
    const handleAddActivity = () => {
        const newId = activities.length > 0 ? Math.max(...activities.map(a => a.id)) + 1 : 1;
        setActivities([...activities, {
            id: newId,
            activity: '',
            date: '',
            location: '',
            volunteers: '',
            isEditing: true
        }]);
    };

    const handleEditActivity = (id) => {
        setActivities(activities.map(activity =>
            activity.id === id ? { ...activity, isEditing: true } : activity
        ));
    };

    const handleSaveActivity = (id) => {
        setActivities(activities.map(activity =>
            activity.id === id ? { ...activity, isEditing: false } : activity
        ));
    };

    const handleCancelActivity = (id) => {
        setActivities(activities.map(activity =>
            activity.id === id ? { ...activity, isEditing: false } : activity
        ));
    };

    const handleDeleteActivity = (id) => {
        setActivities(activities.filter(activity => activity.id !== id));
    };

    const handleActivityChange = (id, field, value) => {
        setActivities(activities.map(activity =>
            activity.id === id ? { ...activity, [field]: value } : activity
        ));
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            dispatch(setLoading({
                loading: true
            }));
            const response = await SubmitIntakeForm({
                "email": formData.academic.email,
                "student_name": formData.academic.studentname,
                "school": formData.academic.schoolname,
                "grade": formData.academic.grade,
                "courses": courses,
                "extracurriculars": activities,
                "calendly_invitee_email": eventData?.invitee_email,
                "calendly_event_uri": eventData?.calendly_event_uri,
                "calendly_invitee_uri": eventData?.calendly_invitee_uri
            });
            if (response.success) {
                showNotification(response.message, 'success');
                setBookSessionDialog(true);
                dispatch(setLoading({
                    loading: false
                }));
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    const getEventData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getIntakeEventByInvite(details?.uri);
            setEventData(response?.data);
            setFormData({
                academic: {
                    studentname: response?.data?.invitee_name,
                    schoolname: '',
                    grade: '',
                    email: response?.data?.invitee_email,
                }
            });
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    useEffect(() => {
        if (details?.uri !== null) {
            dispatch(setLoading({
                loading: true
            }));
            setTimeout(() => {
                getEventData();
            }, 5000);
        }
    }, [details?.uri]);

    return (
        <Box sx={{ p: 4 }}>
            {loading && (
                <LoadingSpinner />
            )}
            <StyledDialog open={bookSessionDialog} onClose={handleCloseAll} maxWidth="sm" fullWidth>
                <DialogContent sx={{ p: 4, position: 'relative' }}>
                    <IconButton
                        onClick={handleCloseAll}
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: 16,
                            color: '#6B7280',
                        }}
                    >
                        <Close />
                    </IconButton>

                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                        Confirm Your Session
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6}>
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: '#F9FAFB',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                }}
                            >
                                <img
                                    src='/Images/graycalendar.png'
                                    style={{
                                        width: 20,
                                        position: 'relative'
                                    }}
                                    alt=''
                                />
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {moment.utc(eventData?.scheduled_start_time).format('dddd, MMM YY')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                        Date
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: '#F9FAFB',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                }}
                            >
                                <WatchLater sx={{ color: '#6B7280', fontSize: 20 }} />
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {moment.utc(eventData?.scheduled_start_time).format('hh:mm A')}- {moment.utc(eventData?.scheduled_end_time).format('hh:mm A')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                        Time
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                    <Button
                        fullWidth
                        onClick={handleCloseAll}
                        sx={{
                            backgroundColor: '#1E3A8A',
                            color: 'white',
                            textTransform: 'none',
                            borderRadius: '8px',
                            height: '48px',
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: '#1E40AF',
                            },
                        }}
                    >
                        Confirm Booking
                    </Button>
                </DialogContent>
            </StyledDialog>
            <Typography textAlign="center" fontSize={26} fontWeight={700}>Intake Form</Typography>
            <Typography textAlign="center" color={theme.palette.background.dark}>Please fill out the student information and share with us</Typography>
            <Grid container spacing={1} sx={{ padding: '13px 0px' }}>
                <Grid item xs={12} md={6}>
                    <CommonFormField
                        label="Student Name"
                        type="email"
                        name="studentname"
                        value={formData.academic.studentname}
                        onChange={handleInputChange}
                        error={errors.studentname}
                        placeholder="Enter student name"
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <CommonFormField
                        label="Student Email"
                        type="email"
                        name="email"
                        value={formData.academic.email}
                        onChange={handleInputChange}
                        error={errors.email}
                        placeholder="Enter email id"
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <CommonFormField
                        label="School Name"
                        type="email"
                        name="schoolname"
                        value={formData.academic.schoolname}
                        onChange={handleInputChange}
                        error={errors.schoolname}
                        placeholder="Enter school name"
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <CommonFormField
                        label="Grade"
                        type="text"
                        name="grade"
                        value={formData.academic.grade}
                        onChange={handleInputChange}
                        error={errors.grade}
                        placeholder="Enter your grade"
                    />
                </Grid>
            </Grid>
            <Box sx={{ padding: '13px 0px' }}>
                <Typography fontWeight={600} mb={1}>
                    Course Summary (Optional)
                </Typography>
                <TableContainer sx={{ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: 1 }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                <Cell width="13%">Grade</Cell>
                                <Cell width="13%">Sem 1</Cell>
                                <Cell width="13%">Full Year Grade</Cell>
                                <Cell width="13%">Sem 2</Cell>
                                <Cell width="13%">Full Year Grade</Cell>
                                <Cell width="13%">Total Sem</Cell>
                                <Cell width="13%">Total\Semesters of H/AP/CL</Cell>
                                <Cell>Delete</Cell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {courses?.map((course) => (
                                <TableRow key={course.id}>
                                    <InnerCell>
                                        {course.isEditing ? (
                                            <CommonFormField
                                                type="email"
                                                value={course.grade}
                                                onChange={(e) => handleCourseChange(course.id, 'grade', e.target.value)}
                                                placeholder=" "
                                            />
                                        ) : (
                                            <Typography>{course.grade}</Typography>
                                        )}
                                    </InnerCell>
                                    <InnerCell>
                                        {course.isEditing ? (
                                            <CommonFormField
                                                type="email"
                                                value={course.sem1}
                                                onChange={(e) => handleCourseChange(course.id, 'sem1', e.target.value)}
                                                placeholder=" "
                                            />
                                        ) : (
                                            <Typography>{course.sem1}</Typography>
                                        )}
                                    </InnerCell>
                                    <InnerCell>
                                        {course.isEditing ? (
                                            <CommonFormField
                                                type="email"
                                                value={course.fullYearGrade1}
                                                onChange={(e) => handleCourseChange(course.id, 'fullYearGrade1', e.target.value)}
                                                placeholder=" "
                                            />
                                        ) : (
                                            <Typography>{course.fullYearGrade1}</Typography>
                                        )}
                                    </InnerCell>
                                    <InnerCell>
                                        {course.isEditing ? (
                                            <CommonFormField
                                                type="email"
                                                value={course.sem2}
                                                onChange={(e) => handleCourseChange(course.id, 'sem2', e.target.value)}
                                                placeholder=" "
                                            />
                                        ) : (
                                            <Typography>{course.sem2}</Typography>
                                        )}
                                    </InnerCell>
                                    <InnerCell>
                                        {course.isEditing ? (
                                            <CommonFormField
                                                type="email"
                                                value={course.fullYearGrade2}
                                                onChange={(e) => handleCourseChange(course.id, 'fullYearGrade2', e.target.value)}
                                                placeholder=" "
                                            />
                                        ) : (
                                            <Typography>{course.fullYearGrade2}</Typography>
                                        )}
                                    </InnerCell>
                                    <InnerCell>
                                        {course.isEditing ? (
                                            <CommonFormField
                                                type="number"
                                                value={course.totalSem}
                                                onChange={(e) => handleCourseChange(course.id, 'totalSem', e.target.value)}
                                                placeholder=" "
                                            />
                                        ) : (
                                            <Typography>{course.totalSem}</Typography>
                                        )}
                                    </InnerCell>
                                    <InnerCell>
                                        {course.isEditing ? (
                                            <CommonFormField
                                                type="number"
                                                value={course.counselor}
                                                onChange={(e) => handleCourseChange(course.id, 'counselor', e.target.value)}
                                                placeholder=" "
                                            />
                                        ) : (
                                            <Typography>{course.counselor}</Typography>
                                        )}
                                    </InnerCell>
                                    <InnerCell>
                                        {course.isEditing ? (
                                            <>
                                                <IconButton size="small" onClick={() => handleSaveCourse(course.id)}>
                                                    <SaveIcon sx={{ color: theme.palette.secondary.main }} />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => handleCancelCourse(course.id)}>
                                                    <Box sx={{ background: 'red', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0.3 }}>
                                                        <Close sx={{ color: 'white', fontSize: 12 }} />
                                                    </Box>
                                                </IconButton>
                                            </>
                                        ) : (
                                            <>
                                                <IconButton size="small" onClick={() => handleDeleteCourse(course.id)}>
                                                    <DeleteForever sx={{ color: 'red', fontSize: 20 }} />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => handleEditCourse(course.id)}>
                                                    <BorderColor sx={{ color: theme.palette.secondary.main, fontSize: 17 }} />
                                                </IconButton>
                                            </>
                                        )}
                                    </InnerCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box mt={1}>
                    <CommonButton
                        label="+ Add a Course"
                        handleClick={handleAddCourse}
                        variant="outlined"
                        size="small"
                        marginBottom="0px"
                        width="15%"
                        height="30px"
                        borderRadius={1}
                        background="white"
                        border={`1px solid ${theme.palette.background.secondary}`}
                        color='black'
                    />
                </Box>
            </Box>
            <Box sx={{ padding: '13px 0px' }}>
                <Typography fontWeight={600} mb={1}>
                    Extracurricular Activities
                </Typography>
                <TableContainer sx={{ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: 1 }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                <Cell width="23%">Activity</Cell>
                                <Cell width="23%">Start Date</Cell>
                                <Cell width="23%">Location(s)</Cell>
                                <Cell width="23%">No Volunteers</Cell>
                                <Cell>Delete</Cell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {activities?.map((activity) => (
                                <TableRow key={activity.id}>
                                    <InnerCell>
                                        {activity.isEditing ? (
                                            <CommonFormField
                                                type="email"
                                                value={activity.activity}
                                                onChange={(e) => handleActivityChange(activity.id, 'activity', e.target.value)}
                                                placeholder=" "
                                            />
                                        ) : (
                                            <Typography>{activity.activity}</Typography>
                                        )}
                                    </InnerCell>
                                    <InnerCell>
                                        {activity.isEditing ? (
                                            <CommonFormField
                                                type="email"
                                                value={activity.date}
                                                onChange={(e) => handleActivityChange(activity.id, 'date', e.target.value)}
                                                placeholder=" "
                                            />
                                        ) : (
                                            <Typography>{moment(activity.date)?.format('DD MMM, YYYY')}</Typography>
                                        )}
                                    </InnerCell>
                                    <InnerCell>
                                        {activity.isEditing ? (
                                            <CommonFormField
                                                type="email"
                                                value={activity.location}
                                                onChange={(e) => handleActivityChange(activity.id, 'location', e.target.value)}
                                                placeholder=" "
                                            />
                                        ) : (
                                            <Typography>{activity.location}</Typography>
                                        )}
                                    </InnerCell>
                                    <InnerCell>
                                        {activity.isEditing ? (
                                            <CommonFormField
                                                type="number"
                                                value={activity.volunteers}
                                                onChange={(e) => handleActivityChange(activity.id, 'volunteers', e.target.value)}
                                                placeholder=" "
                                            />
                                        ) : (
                                            <Typography>{activity.volunteers}</Typography>
                                        )}
                                    </InnerCell>
                                    <InnerCell>
                                        {activity.isEditing ? (
                                            <>
                                                <IconButton size="small" onClick={() => handleSaveActivity(activity.id)}>
                                                    <SaveIcon sx={{ color: theme.palette.secondary.main }} />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => handleCancelActivity(activity.id)}>
                                                    <Box sx={{ background: 'red', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0.3 }}>
                                                        <Close sx={{ color: 'white', fontSize: 12 }} />
                                                    </Box>
                                                </IconButton>
                                            </>
                                        ) : (
                                            <>
                                                <IconButton size="small" onClick={() => handleDeleteActivity(activity.id)}>
                                                    <DeleteForever sx={{ color: 'red', fontSize: 20 }} />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => handleEditActivity(activity.id)}>
                                                    <BorderColor sx={{ color: theme.palette.secondary.main, fontSize: 17 }} />
                                                </IconButton>
                                            </>
                                        )}
                                    </InnerCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box mt={1}>
                    <CommonButton
                        label="+ Add an Activity"
                        handleClick={handleAddActivity}
                        variant="outlined"
                        size="small"
                        width="15%"
                        height="30px"
                        borderRadius={1}
                        marginBottom="0px"
                        background="white"
                        color='black'
                        border={`1px solid ${theme.palette.background.secondary}`}
                    />
                </Box>
            </Box>
            <Box
                display="flex"
                justifyContent="flex-end"
                width="100%"
                gap={1}
            >
                <CommonButton
                    label="Skip"
                    background="White"
                    color={theme.palette.background.dark}
                    border={`1px solid #0000001A`}
                    handleClick={async () => {
                        try {
                            dispatch(setLoading({
                                loading: true
                            }));
                            const response = await SkipIntakeForm({
                                email: formData.academic.email,
                                student_name: formData.academic.studentname,
                                "calendly_invitee_email": eventData?.invitee_email,
                                "calendly_event_uri": eventData?.calendly_event_uri,
                                "calendly_invitee_uri": eventData?.calendly_invitee_uri
                            });
                            if (response.success) {
                                setBookSessionDialog(true);
                                dispatch(setLoading({
                                    loading: false
                                }));
                            }
                        } catch (err) {
                            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                            showNotification(errorMessage, 'error');
                            dispatch(setLoading({
                                loading: false
                            }));
                        }
                    }}
                    marginBottom="10px"
                    fontWeight={600}
                    height="40px"
                    width="100px"
                    fontSize="16px"
                />
                <CommonButton
                    label="Submit Demo Request"
                    handleClick={handleSubmit}
                    marginBottom="10px"
                    fontWeight={600}
                    height="40px"
                    width="240px"
                    fontSize="16px"
                />
            </Box>
        </Box >
    )
}

export default IntakeForm;